import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from '../../stores/organization.store';
import { getUsers } from '../../services/auth.service';
import {
  getOrganizationTrainingSummary,
  normalizeTrainingSummaryResult,
} from '../../services/employee-training.service';
import type { UserSchema } from '../../types/auth.type';
import type { GenericResponse, OrganizationEmployeeTrainingSummaryItem } from '../../types/employee-training.type';
import RecordTrainingDialog from '../../components/employee-training/RecordTrainingDialog';
import TrainingTableToolbar from '../../components/employee-training/TrainingTableToolbar';
import PageHeader from '../../components/PageHeader';
import { SetupCoachmark } from '../../components/selfSetup/SetupCoachmark';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import {
  HELP_GUIDE_RECORD_TRAINING,
  HELP_GUIDE_TRAINING_DETAIL,
  HELP_GUIDE_TRAINING_OVERVIEW,
} from '../../constants/knowledgeBaseTryIt';
import { helpI18n } from '../../i18n/help';
import {
  clearHelpGuideActive,
  getActiveHelpGuide,
  getTrainingOverviewHelpPhase,
  markHelpGuideActive,
  setTrainingDetailHelpPhase,
  setTrainingOverviewHelpPhase,
  type TrainingOverviewHelpPhase,
} from '../../utils/helpGuideSession';
import { trainingBodyCellSx } from '../../components/employee-training/trainingTable.styles';
import {
  cellOrDash,
  employeeDisplayName,
  enrichTrainingSummaryList,
  fullNameForTable,
  isPlaceholderCell,
  matchesEmployeeLevelFilter,
  organizationUsersQueryKey,
  overviewRowSearchHay,
  parseTrainingHours,
  pruneEmployeeLevelFilter,
} from '../../utils/employee-training.util';

type Order = 'asc' | 'desc';

type OverviewSortKey =
  | 'full_name'
  | 'username'
  | 'phone'
  | 'employee_level'
  | 'training_hours_total'
  | 'session_count'
  | 'last_session_date';

const TABLE_COL_COUNT = 8;

const overviewCell = {
  text: { align: 'left' as const, sx: { whiteSpace: 'nowrap' } },
  ltr: {
    align: 'left' as const,
    sx: { direction: 'ltr', unicodeBidi: 'plaintext', whiteSpace: 'nowrap' },
  },
  numeric: {
    align: 'right' as const,
    sx: { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  },
  date: {
    align: 'left' as const,
    sx: { direction: 'ltr', unicodeBidi: 'plaintext', whiteSpace: 'nowrap' },
  },
  actions: { align: 'right' as const },
};

function compareStrings(a: string | null | undefined, b: string | null | undefined): number {
  return (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' });
}

function compareOverviewRows(
  a: OrganizationEmployeeTrainingSummaryItem,
  b: OrganizationEmployeeTrainingSummaryItem,
  orderBy: OverviewSortKey,
): number {
  switch (orderBy) {
    case 'full_name':
      return fullNameForTable(a).localeCompare(fullNameForTable(b), undefined, { sensitivity: 'base' });
    case 'username':
      return compareStrings(a.username, b.username);
    case 'phone':
      return compareStrings(a.phone, b.phone);
    case 'employee_level':
      return compareStrings(a.employee_level, b.employee_level);
    case 'training_hours_total':
      return parseTrainingHours(a.training_hours_total) - parseTrainingHours(b.training_hours_total);
    case 'session_count':
      return a.session_count - b.session_count;
    case 'last_session_date': {
      const da = a.last_session_date ?? '';
      const db = b.last_session_date ?? '';
      return da.localeCompare(db);
    }
    default:
      return 0;
  }
}

const EmployeeTrainingOverview: React.FC = () => {
  const { t } = useTranslation();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization } = useOrganizationStore();
  const organizationId = selectedOrganization?.id ?? '';

  const [helpGuide, setHelpGuide] = useState<string | null>(null);
  const [overviewPhase, setOverviewPhase] = useState<TrainingOverviewHelpPhase>('filter');
  const [recordTourPhase, setRecordTourPhase] = useState<'button' | 'form' | 'save'>('button');
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordUserId, setRecordUserId] = useState<string | null>(null);
  const [recordLabel, setRecordLabel] = useState<string>('');

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OverviewSortKey>('full_name');
  const [filterText, setFilterText] = useState('');
  const [levelFilter, setLevelFilter] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const isOverviewTour = helpGuide === HELP_GUIDE_TRAINING_OVERVIEW;
  const showOverviewFilterTip = isOverviewTour && overviewPhase === 'filter';
  const showOverviewTableTip = isOverviewTour && overviewPhase === 'table';
  const showOverviewViewHistoryTip =
    isOverviewTour && overviewPhase === 'viewHistory';
  const showOverviewAddTrainingTip =
    isOverviewTour && overviewPhase === 'addTraining';
  const showRecordButtonTip =
    (helpGuide === HELP_GUIDE_RECORD_TRAINING && recordTourPhase === 'button') ||
    showOverviewAddTrainingTip;
  const showRecordFormTip =
    (helpGuide === HELP_GUIDE_RECORD_TRAINING &&
      recordTourPhase === 'form' &&
      recordOpen) ||
    (isOverviewTour && overviewPhase === 'recordForm' && recordOpen);
  const showRecordSaveTip =
    (helpGuide === HELP_GUIDE_RECORD_TRAINING &&
      recordTourPhase === 'save' &&
      recordOpen) ||
    (isOverviewTour && overviewPhase === 'recordSave' && recordOpen);
  const showTrainingDetailTip = helpGuide === HELP_GUIDE_TRAINING_DETAIL;

  const finishHelpGuide = () => {
    setHelpGuide(null);
    setOverviewPhase('filter');
    setRecordTourPhase('button');
    clearHelpGuideActive();
  };

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (
      guide !== HELP_GUIDE_TRAINING_OVERVIEW &&
      guide !== HELP_GUIDE_RECORD_TRAINING &&
      guide !== HELP_GUIDE_TRAINING_DETAIL
    ) {
      return;
    }
    markHelpGuideActive(guide);
    setHelpGuide(guide);
    setRecordTourPhase('button');
    if (guide === HELP_GUIDE_TRAINING_DETAIL) {
      setTrainingDetailHelpPhase('view');
    }
    if (guide === HELP_GUIDE_TRAINING_OVERVIEW) {
      setTrainingOverviewHelpPhase('filter');
      setOverviewPhase('filter');
    }
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Resume overview tour after returning from detail (addTraining phase).
  useEffect(() => {
    if (helpGuide) return;
    if (getActiveHelpGuide() !== HELP_GUIDE_TRAINING_OVERVIEW) return;
    const phase = getTrainingOverviewHelpPhase();
    if (phase === 'addTraining' || phase === 'recordForm' || phase === 'recordSave') {
      setHelpGuide(HELP_GUIDE_TRAINING_OVERVIEW);
      setOverviewPhase(phase);
    }
  }, [helpGuide, location.pathname]);

  useEffect(() => {
    if (!helpGuide) return;
    const id =
      showOverviewFilterTip
        ? 'help-training-overview-filter'
        : showOverviewTableTip
          ? 'help-training-overview-table'
          : showOverviewViewHistoryTip || showTrainingDetailTip
            ? 'help-training-detail'
            : showRecordSaveTip
              ? 'help-training-record-save'
              : showRecordFormTip
                ? 'help-training-record-form'
                : 'help-training-record';
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [
    helpGuide,
    overviewPhase,
    recordTourPhase,
    showOverviewFilterTip,
    showOverviewTableTip,
    showOverviewViewHistoryTip,
    showTrainingDetailTip,
    showRecordFormTip,
    showRecordSaveTip,
  ]);

  const trainingQueryRetry = (failureCount: number, err: unknown) => {
    const status = (err as AxiosError).response?.status;
    if (status === 403 || status === 404) return false;
    return failureCount < 2;
  };

  const summaryQuery = useQuery({
    queryKey: ['training-summary', organizationId],
    queryFn: async () => {
      const res = await getOrganizationTrainingSummary(organizationId);
      return normalizeTrainingSummaryResult(res);
    },
    enabled: Boolean(organizationId),
    retry: trainingQueryRetry,
  });

  const usersQuery = useQuery({
    queryKey: organizationUsersQueryKey(organizationId),
    queryFn: async () => {
      const res = (await getUsers(organizationId)) as GenericResponse<UserSchema[]>;
      return res.result ?? [];
    },
    enabled: Boolean(organizationId),
    staleTime: 5 * 60 * 1000,
    retry: trainingQueryRetry,
  });

  const forbidden = useMemo(() => {
    const err = summaryQuery.error as AxiosError | undefined;
    return err?.response?.status === 403;
  }, [summaryQuery.error]);

  const sourceRows = useMemo(
    () => enrichTrainingSummaryList(summaryQuery.data?.result_list ?? [], usersQuery.data ?? []),
    [summaryQuery.data, usersQuery.data],
  );

  const filteredSortedRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    let data = [...sourceRows];
    if (q) {
      data = data.filter((row) => overviewRowSearchHay(row).includes(q));
    }
    if (levelFilter.length > 0) {
      data = data.filter((row) => matchesEmployeeLevelFilter(row, levelFilter));
    }
    const mult = order === 'asc' ? 1 : -1;
    data.sort((a, b) => mult * compareOverviewRows(a, b, orderBy));
    return data;
  }, [sourceRows, filterText, levelFilter, order, orderBy]);

  useEffect(() => {
    setLevelFilter((prev) => {
      const next = pruneEmployeeLevelFilter(prev);
      return next.length === prev.length ? prev : next;
    });
  }, []);

  useEffect(() => {
    setPage(0);
  }, [filterText, levelFilter, sourceRows]);

  const paginatedRows = useMemo(
    () => filteredSortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredSortedRows, page, rowsPerPage],
  );

  const advanceRecordGuide = () => {
    if (isOverviewTour) {
      if (overviewPhase === 'addTraining' || overviewPhase === 'recordForm') {
        if (overviewPhase === 'addTraining') {
          const first = paginatedRows[0] ?? filteredSortedRows[0];
          if (first) {
            setRecordUserId(first.user_id);
            setRecordLabel(employeeDisplayName(first));
            setRecordOpen(true);
          }
          setTrainingOverviewHelpPhase('recordForm');
          setOverviewPhase('recordForm');
          return;
        }
        setTrainingOverviewHelpPhase('recordSave');
        setOverviewPhase('recordSave');
        return;
      }
      finishHelpGuide();
      return;
    }
    if (recordTourPhase === 'button') {
      const first = paginatedRows[0] ?? filteredSortedRows[0];
      if (first) {
        setRecordUserId(first.user_id);
        setRecordLabel(employeeDisplayName(first));
        setRecordOpen(true);
      }
      setRecordTourPhase('form');
      return;
    }
    if (recordTourPhase === 'form') {
      setRecordTourPhase('save');
      return;
    }
    finishHelpGuide();
  };

  const openDetailForHelp = (row: OrganizationEmployeeTrainingSummaryItem) => {
    if (isOverviewTour) {
      setTrainingOverviewHelpPhase('detailSummary');
      markHelpGuideActive(HELP_GUIDE_TRAINING_OVERVIEW);
    } else {
      setTrainingDetailHelpPhase('summary');
      markHelpGuideActive(HELP_GUIDE_TRAINING_DETAIL);
    }
    navigate(`/employee-training/${row.user_id}`, {
      state: { employeeLabel: employeeDisplayName(row) },
    });
  };

  const advanceTrainingDetailGuide = () => {
    const first = paginatedRows[0] ?? filteredSortedRows[0];
    if (!first) {
      finishHelpGuide();
      return;
    }
    openDetailForHelp(first);
  };

  const advanceOverviewGuide = () => {
    if (overviewPhase === 'filter') {
      setTrainingOverviewHelpPhase('table');
      setOverviewPhase('table');
      return;
    }
    if (overviewPhase === 'table') {
      setTrainingOverviewHelpPhase('viewHistory');
      setOverviewPhase('viewHistory');
      return;
    }
    if (overviewPhase === 'viewHistory') {
      advanceTrainingDetailGuide();
      return;
    }
    if (overviewPhase === 'addTraining') {
      advanceRecordGuide();
      return;
    }
    if (overviewPhase === 'recordForm') {
      advanceRecordGuide();
      return;
    }
    finishHelpGuide();
  };

  const handleRequestSort = (property: OverviewSortKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const openRecordFor = (row: OrganizationEmployeeTrainingSummaryItem) => {
    setRecordUserId(row.user_id);
    setRecordLabel(employeeDisplayName(row));
    setRecordOpen(true);
    if (helpGuide === HELP_GUIDE_RECORD_TRAINING) {
      setRecordTourPhase('form');
    }
    if (isOverviewTour && overviewPhase === 'addTraining') {
      setTrainingOverviewHelpPhase('recordForm');
      setOverviewPhase('recordForm');
    }
  };

  const closeRecordDialog = () => {
    setRecordOpen(false);
    if (
      helpGuide === HELP_GUIDE_RECORD_TRAINING &&
      (recordTourPhase === 'form' || recordTourPhase === 'save')
    ) {
      finishHelpGuide();
    }
    if (
      isOverviewTour &&
      (overviewPhase === 'recordForm' || overviewPhase === 'recordSave')
    ) {
      finishHelpGuide();
    }
  };

  const goToDetail = (row: OrganizationEmployeeTrainingSummaryItem) => {
    if (helpGuide === HELP_GUIDE_TRAINING_DETAIL || showOverviewViewHistoryTip) {
      openDetailForHelp(row);
      return;
    }
    navigate(`/employee-training/${row.user_id}`, {
      state: { employeeLabel: employeeDisplayName(row) },
    });
  };

  const titleCard = (
    <PageHeader icon={<SchoolOutlinedIcon />} title={t('employeeTraining.title')} />
  );

  if (!selectedOrganization) {
    return (
      <Box>
        {titleCard}
        <Alert severity="info">{t('employeeTraining.selectOrganizationView')}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {titleCard}

      {(summaryQuery.isFetching || usersQuery.isFetching) && <LinearProgress sx={{ mb: 2 }} />}

      {forbidden ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('employeeTraining.forbidden')}
        </Alert>
      ) : null}

      {!forbidden && summaryQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(summaryQuery.error as AxiosError<{ detail?: string }>)?.response?.data?.detail ||
            (summaryQuery.error as Error).message ||
            t('employeeTraining.loadError')}
        </Alert>
      ) : null}

      {!forbidden && !summaryQuery.isError ? (
        <Box id="help-training-overview">
          <SetupCoachmark
            open={showOverviewFilterTip}
            title={tHelp('knowledgeBase.trainingOverviewFilterTitle')}
            subtitle={tHelp('knowledgeBase.trainingOverviewFilterSubtitle')}
            onDismiss={finishHelpGuide}
            onNext={advanceOverviewGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
            placement="bottom"
          >
            <Box id="help-training-overview-filter">
              <TrainingTableToolbar
                filterText={filterText}
                onFilterTextChange={setFilterText}
                levelFilter={levelFilter}
                onLevelFilterChange={setLevelFilter}
              />
            </Box>
          </SetupCoachmark>
          <SetupCoachmark
            open={showOverviewTableTip}
            title={tHelp('knowledgeBase.trainingOverviewTableTitle')}
            subtitle={tHelp('knowledgeBase.trainingOverviewTableSubtitle')}
            onDismiss={finishHelpGuide}
            onNext={advanceOverviewGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
            placement="top"
          >
          <Paper id="help-training-overview-table" sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: { md: 'calc(100vh - 320px)' } }}>
            <Table
              stickyHeader
              aria-label={t('employeeTraining.tableAria.overview')}
              sx={{ minWidth: 960 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    align={overviewCell.text.align}
                    sortDirection={orderBy === 'full_name' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'full_name'}
                      direction={orderBy === 'full_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('full_name')}
                    >
                      {t('employeeTraining.columns.fullName')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align={overviewCell.ltr.align}
                    sortDirection={orderBy === 'username' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'username'}
                      direction={orderBy === 'username' ? order : 'asc'}
                      onClick={() => handleRequestSort('username')}
                    >
                      {t('employeeTraining.columns.username')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align={overviewCell.ltr.align}
                    sortDirection={orderBy === 'phone' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'phone'}
                      direction={orderBy === 'phone' ? order : 'asc'}
                      onClick={() => handleRequestSort('phone')}
                    >
                      {t('employeeTraining.columns.phone')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align={overviewCell.text.align}
                    sortDirection={orderBy === 'employee_level' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'employee_level'}
                      direction={orderBy === 'employee_level' ? order : 'asc'}
                      onClick={() => handleRequestSort('employee_level')}
                    >
                      {t('employeeTraining.columns.level')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align={overviewCell.numeric.align}
                    sortDirection={orderBy === 'training_hours_total' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'training_hours_total'}
                      direction={orderBy === 'training_hours_total' ? order : 'asc'}
                      onClick={() => handleRequestSort('training_hours_total')}
                    >
                      {t('employeeTraining.columns.totalHours')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align={overviewCell.numeric.align}
                    sortDirection={orderBy === 'session_count' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'session_count'}
                      direction={orderBy === 'session_count' ? order : 'asc'}
                      onClick={() => handleRequestSort('session_count')}
                    >
                      {t('employeeTraining.columns.sessions')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align={overviewCell.date.align}
                    sortDirection={orderBy === 'last_session_date' ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === 'last_session_date'}
                      direction={orderBy === 'last_session_date' ? order : 'asc'}
                      onClick={() => handleRequestSort('last_session_date')}
                    >
                      {t('employeeTraining.columns.lastTraining')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align={overviewCell.actions.align}
                  >
                    {t('employeeTraining.columns.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sourceRows.length === 0 && !summaryQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={TABLE_COL_COUNT}>
                      <Typography color="text.secondary" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
                        {t('employeeTraining.noEmployees')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {sourceRows.length > 0 && paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={TABLE_COL_COUNT}>
                      <Typography color="text.secondary" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
                        {t('employeeTraining.noFilterMatch')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {paginatedRows.map((row) => {
                  const fullName = fullNameForTable(row);
                  const username = cellOrDash(row.username);
                  const phone = cellOrDash(row.phone);
                  const level = cellOrDash(row.employee_level);
                  const lastDate = cellOrDash(row.last_session_date);
                  const levelMissing = isPlaceholderCell(level);
                  const isFirstRow = paginatedRows[0]?.user_id === row.user_id;
                  const spotlightAddTraining =
                    isFirstRow && showRecordButtonTip;
                  const spotlightViewHistory =
                    isFirstRow &&
                    (showTrainingDetailTip || showOverviewViewHistoryTip);
                  return (
                  <TableRow key={row.user_id} hover>
                    <TableCell
                      align={overviewCell.text.align}
                      sx={trainingBodyCellSx(isPlaceholderCell(fullName), overviewCell.text.sx)}
                      title={fullName}
                    >
                      {fullName}
                    </TableCell>
                    <TableCell
                      align={overviewCell.ltr.align}
                      sx={trainingBodyCellSx(isPlaceholderCell(username), overviewCell.ltr.sx)}
                      title={username}
                    >
                      {username}
                    </TableCell>
                    <TableCell
                      align={overviewCell.ltr.align}
                      sx={trainingBodyCellSx(isPlaceholderCell(phone), overviewCell.ltr.sx)}
                      title={phone}
                    >
                      {phone}
                    </TableCell>
                    <TableCell
                      align={overviewCell.text.align}
                      sx={trainingBodyCellSx(levelMissing)}
                    >
                      {levelMissing ? (
                        level
                      ) : (
                        <Chip label={level} size="small" />
                      )}
                    </TableCell>
                    <TableCell
                      align={overviewCell.numeric.align}
                      sx={trainingBodyCellSx(false, overviewCell.numeric.sx)}
                    >
                      {parseTrainingHours(row.training_hours_total).toFixed(2)}
                    </TableCell>
                    <TableCell
                      align={overviewCell.numeric.align}
                      sx={trainingBodyCellSx(false, overviewCell.numeric.sx)}
                    >
                      {row.session_count}
                    </TableCell>
                    <TableCell
                      align={overviewCell.date.align}
                      sx={trainingBodyCellSx(isPlaceholderCell(lastDate), overviewCell.date.sx)}
                    >
                      {lastDate}
                    </TableCell>
                    <TableCell align={overviewCell.actions.align} sx={{ whiteSpace: 'nowrap' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          justifyContent: 'flex-end',
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                        }}
                      >
                        <SetupCoachmark
                          open={
                            (showTrainingDetailTip || showOverviewViewHistoryTip) &&
                            isFirstRow
                          }
                          title={tHelp('knowledgeBase.trainingDetailGuideTitle')}
                          subtitle={tHelp('knowledgeBase.trainingDetailGuideSubtitle')}
                          onDismiss={finishHelpGuide}
                          onNext={
                            showOverviewViewHistoryTip
                              ? advanceOverviewGuide
                              : advanceTrainingDetailGuide
                          }
                          nextLabel={t('selfSetup.tourNext')}
                          compact
                          placement="left"
                        >
                        <Tooltip title={t('employeeTraining.viewHistory')}>
                          <Box
                            component="span"
                            {...(spotlightViewHistory
                              ? { 'data-setup-hole': true }
                              : {})}
                            sx={{ display: 'inline-flex' }}
                          >
                            <IconButton
                              id={
                                spotlightViewHistory
                                  ? 'help-training-detail'
                                  : undefined
                              }
                              size="small"
                              color="primary"
                              onClick={() => goToDetail(row)}
                              disabled={forbidden}
                              aria-label={t('employeeTraining.viewHistory')}
                            >
                              <HistoryOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Tooltip>
                        </SetupCoachmark>
                        <SetupCoachmark
                          open={showRecordButtonTip && isFirstRow}
                          title={tHelp('knowledgeBase.recordTrainingGuideTitle')}
                          subtitle={tHelp('knowledgeBase.recordTrainingGuideSubtitle')}
                          onDismiss={finishHelpGuide}
                          onNext={
                            isOverviewTour ? advanceOverviewGuide : advanceRecordGuide
                          }
                          nextLabel={t('selfSetup.tourNext')}
                          compact
                          placement="left"
                        >
                          <Tooltip title={t('employeeTraining.addTraining')}>
                            <Box
                              component="span"
                              {...(spotlightAddTraining
                                ? { 'data-setup-hole': true }
                                : {})}
                              sx={{ display: 'inline-flex' }}
                              id={
                                isFirstRow ? 'help-training-record' : undefined
                              }
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openRecordFor(row)}
                                disabled={forbidden}
                                aria-label={t('employeeTraining.addTraining')}
                              >
                                <AddOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </SetupCoachmark>
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={t('common.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('common.rangeOf', { from, to, count })}
            count={filteredSortedRows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            />
        </Paper>
          </SetupCoachmark>
          </Box>
      ) : null}

      {recordUserId ? (
        <RecordTrainingDialog
          open={recordOpen}
          onClose={() => {
            closeRecordDialog();
            setRecordUserId(null);
            setRecordLabel('');
          }}
          organizationId={organizationId}
          userId={recordUserId}
          employeeLabel={recordLabel}
          formHelpTip={{
            open: showRecordFormTip,
            title: tHelp('knowledgeBase.recordTrainingFormGuideTitle'),
            subtitle: tHelp('knowledgeBase.recordTrainingFormGuideSubtitle'),
            onDismiss: finishHelpGuide,
            onNext: advanceRecordGuide,
            nextLabel: t('selfSetup.tourNext'),
          }}
          saveHelpTip={{
            open: showRecordSaveTip,
            title: tHelp('knowledgeBase.recordTrainingSaveGuideTitle'),
            subtitle: tHelp('knowledgeBase.recordTrainingSaveGuideSubtitle'),
            onDismiss: finishHelpGuide,
            onNext: finishHelpGuide,
            nextLabel: t('selfSetup.tourDone'),
          }}
        />
      ) : null}
    </Box>
  );
};

export default EmployeeTrainingOverview;
